import { useQueries, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { isValidAccessToken } from '../../helpers/storage/storage';
import { getSubprojectStatuses } from './subprojects-client';
import type { StatusesBySubprojectId } from './subprojects-client.type';

/**
 * Status names change only when a customer edits their board workflow, so a
 * generous stale window keeps the per-board fan-out from re-requesting on
 * every mount of every status-rendering surface.
 */
const STATUSES_STALE_TIME_MS = 1000 * 60 * 5;

/**
 * Shared empty result returned whenever a board has no data yet (in flight,
 * disabled, or failed).
 *
 * Referential stability matters: consumers feed `statuses` straight into a
 * `useMemo` whose output feeds an effect that calls `setState`, so a fresh
 * `[]` per render would never let that cycle settle. The React Compiler
 * currently memoizes the hook body, but it skips test and story files and
 * could be reconfigured, so the guarantee is made explicit here rather than
 * left to the build.
 */
const NO_STATUSES: readonly string[] = [];

/**
 * Builds the shared React Query key for one board's status names.
 *
 * Exported so the per-board and multi-board hooks provably read and write the
 * same cache entries — a surface that already fetched a board via
 * `useSubprojectStatuses` serves the multi-board hook from cache.
 *
 * @param {string} subprojectId - The board/subproject UUID
 * @returns {string[]} The query key for that board's statuses
 */
const subprojectStatusesQueryKey = (subprojectId: string): string[] => ['subprojectStatuses', subprojectId];

/**
 * Retries transport failures but never client errors.
 *
 * A `404` here is a definitive answer — the board is missing, malformed, or
 * the user cannot see it — so retrying only delays the raw-casing fallback.
 *
 * @param {number} failureCount - How many attempts have already failed
 * @param {Error} error - The rejection value, in practice an AxiosError carrying `response.status`
 * @returns {boolean} Whether React Query should attempt again
 */
const shouldRetryStatusesRequest = (failureCount: number, error: Error): boolean => {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < 2;
};

/**
 * Query manager for a single board's workflow status names in source casing.
 *
 * @param {string | null | undefined} subprojectId - The board to fetch; the query stays idle while nullish
 * @returns {{ statuses: readonly string[]; query: UseQueryResult<string[]> }} Status names (a stable empty array until loaded or on failure) and the query result
 */
const useSubprojectStatuses = (
  subprojectId: string | null | undefined,
): { statuses: readonly string[]; query: UseQueryResult<string[]> } => {
  const query = useQuery({
    queryKey: subprojectStatusesQueryKey(subprojectId ?? ''),
    queryFn: () => getSubprojectStatuses(subprojectId as string),
    enabled: Boolean(subprojectId) && isValidAccessToken(),
    staleTime: STATUSES_STALE_TIME_MS,
    retry: shouldRetryStatusesRequest,
  });

  return { statuses: query.data ?? NO_STATUSES, query };
};

/**
 * Query manager for several boards' workflow status names, fetched in parallel.
 *
 * Responsibilities:
 * 1. De-duplicate and drop empty ids so a repeated board is requested once.
 * 2. Fan out one request per board, sharing cache entries with
 *    `useSubprojectStatuses` via the same query key.
 * 3. Combine the results into an id-keyed record, omitting boards that failed
 *    so callers can distinguish "no labels for this board" from "empty board"
 *    only if they care — both degrade to raw casing downstream.
 *
 * A board that 404s contributes no entry rather than failing the whole set;
 * partial results are the expected steady state while boards are backfilled.
 *
 * @param {(string | null | undefined)[]} subprojectIds - Boards to fetch; nullish and duplicate entries are ignored
 * @returns {{ statusesBySubprojectId: StatusesBySubprojectId; isLoading: boolean }} Per-board statuses and whether any request is still in flight
 */
const useSubprojectStatusesForBoards = (
  subprojectIds: (string | null | undefined)[],
): { statusesBySubprojectId: StatusesBySubprojectId; isLoading: boolean } => {
  const uniqueIds = Array.from(new Set(subprojectIds.filter((id): id is string => Boolean(id))));

  return useQueries({
    queries: uniqueIds.map((subprojectId) => ({
      queryKey: subprojectStatusesQueryKey(subprojectId),
      queryFn: () => getSubprojectStatuses(subprojectId),
      enabled: isValidAccessToken(),
      staleTime: STATUSES_STALE_TIME_MS,
      retry: shouldRetryStatusesRequest,
    })),
    combine: (results) => {
      const statusesBySubprojectId: StatusesBySubprojectId = {};
      results.forEach((result, index) => {
        if (result.data) {
          statusesBySubprojectId[uniqueIds[index]] = result.data;
        }
      });
      return {
        statusesBySubprojectId,
        isLoading: results.some((result) => result.isLoading),
      };
    },
  });
};

export { subprojectStatusesQueryKey, useSubprojectStatuses, useSubprojectStatusesForBoards };
